import { EVENTS } from 'xgplayer-helper-utils'
import SeiOnDemand from './sei-ondemand'
import { CORE_EVENT } from './config'
const REMUX_EVENTS = EVENTS.REMUX_EVENTS
const DEMUX_EVENTS = EVENTS.DEMUX_EVENTS
const LOADER_EVENTS = EVENTS.LOADER_EVENTS
const MSE_EVENTS = EVENTS.MSE_EVENTS

const Tag = 'FLVController'

const FLV_ERROR = 'FLV_ERROR'

/**
 * @typedef {{
 *  stauts: 'done' | 'nobuffer',
 *  readyStateWhenComplete: number,
 *  endOfStream: boolean,
 *  mock?: boolean,
 *  currentTime?: number,
 *  bufferEnd?: number
 * }} EndedStatus
 */

export default class FlvController {
  constructor (mse, configs) {
    this.TAG = Tag
    this.state = {
      initSegmentArrived: false,
      randomAccessPoints: []
    }

    this.bufferClearTimer = null

    this._handleTimeUpdate = this._handleTimeUpdate.bind(this)
    this._handleKeyFrame = this._handleKeyFrame.bind(this)

    this.mse = mse
    this.configs = configs || {}

    /**
     * @type {EndedStatus}
     */
    this._endedStatus = null
  }

  get tracks () {
    return this._context.getInstance('TRACKS')
  }

  get endedStatus () {
    return this._endedStatus
  }

  init () {
    if (!this.mse) {
      const { Mse } = this._pluginConfig
      this.mse = new Mse({ container: this._player.video }, this._context)
      this.mse.init()
    }
    if (this._pluginConfig.seiOnDemand) {
      this._seiOnDemand = new SeiOnDemand(this._player)
    }
    this.initComponents()
    this.initListeners()
  }

  initComponents () {
    const { FetchLoader, XgBuffer, FlvDemuxer, Tracks, Remuxer, RemuxedBufferManager, Compatibility, Logger } = this._pluginConfig
    const { remux: remuxConfig } = this.configs

    this._context.registry('FETCH_LOADER', FetchLoader)
    this._context.registry('LOADER_BUFFER', XgBuffer)

    this._context.registry('FLV_DEMUXER', FlvDemuxer)
    this._context.registry('TRACKS', Tracks)

    // 目的是abr场景下 按原来的baseDts进行新流的时间戳偏移. FIXME！
    if (remuxConfig) {
      const remuxer = this._context.registry('MP4_REMUXER', Remuxer)(this._player.currentTime).remuxer
      Object.keys(remuxConfig).forEach(key => {
        remuxer[key] = remuxConfig[key]
      })
      // use origin videoDtsBase derect
      this._seiOnDemand?.updateBaseDts(remuxConfig._videoDtsBase)
    } else {
      this._context.registry('MP4_REMUXER', Remuxer)()
    }

    this._context.registry('PRE_SOURCE_BUFFER', RemuxedBufferManager)

    this._context.registry('COMPATIBILITY', Compatibility)

    this._context.registry('LOGGER', Logger)
  }

  initListeners () {
    this.on(LOADER_EVENTS.LOADER_DATALOADED, this._handleLoaderDataLoaded.bind(this))
    this.on(LOADER_EVENTS.LOADER_ERROR, this._handleNetworkError.bind(this))
    this.on(LOADER_EVENTS.LOADER_RETRY, this._handleFetchRetry.bind(this))
    this.on(LOADER_EVENTS.LOADER_TTFB, this._handleTTFB.bind(this))
    this.once(LOADER_EVENTS.LOADER_COMPLETE, this._handleLoadComplete.bind(this))

    this.on(DEMUX_EVENTS.MEDIA_INFO, this._handleMediaInfo.bind(this))
    this.on(DEMUX_EVENTS.METADATA_PARSED, this._handleMetadataParsed.bind(this))
    this.on(DEMUX_EVENTS.DEMUX_COMPLETE, this._handleDemuxComplete.bind(this))
    this.on(DEMUX_EVENTS.DEMUX_ERROR, this._handleDemuxError.bind(this))
    this.on(DEMUX_EVENTS.SEI_PARSED, this._handleSEIParsed.bind(this))

    this.on(REMUX_EVENTS.INIT_SEGMENT, this._handleAppendInitSegment.bind(this))
    this.on(REMUX_EVENTS.MEDIA_SEGMENT, this._handleMediaSegment.bind(this))
    this.on(REMUX_EVENTS.RANDOM_ACCESS_POINT, this._handleAddRAP.bind(this))
    this.on(REMUX_EVENTS.REMUX_ERROR, this._handleRemuxError.bind(this))

    this.on(MSE_EVENTS.SOURCE_UPDATE_END, this._handleSourceUpdateEnd.bind(this))
    this.on(MSE_EVENTS.MSE_ERROR, this._handleMseError.bind(this))
    this.on(DEMUX_EVENTS.ISKEYFRAME, this._handleKeyFrame)

    if (!this.configs.remux) {
      // keep origin baseDts when change stream use abr mode
      this.once(DEMUX_EVENTS.ISKEYFRAME, (pts, cts) => {
        this._seiOnDemand?.updateBaseDts(pts - cts)
      })
    }

    this._player.on('timeupdate', this._handleTimeUpdate)
  }

  _handleMediaInfo () {
    if (!this._context.mediaInfo) {
      this.emit(DEMUX_EVENTS.DEMUX_ERROR, new Error('failed to get mediainfo'))
    }
  }

  _handleKeyFrame (pts) {
    this._player?.emit(CORE_EVENT.KEYFRAME, pts)
  }

  _handleTTFB (info) {
    this._player?.emit(CORE_EVENT.TTFB, info)
  }

  _handleLoaderDataLoaded () {
    this.emitTo('FLV_DEMUXER', DEMUX_EVENTS.DEMUX_START)
  }

  _handleLoadComplete (status) {
    let player = this._player
    if (!player) return

    /**
     * @type {EndedStatus}
     */
    this._endedStatus = {
      status,
      readyStateWhenComplete: player.video?.readyState,
      endOfStream: player.video?.readyState > 2
    }

    this.pause()

    // mse endofstream then video can emit ended auto
    if (player.video?.readyState > 2) {
      this._endOfStream()
    }

    let lastCurrentTime = 0

    const checkEnd = () => {
      if (!player || !player.video || player.ended) return

      if (player.currentTime === lastCurrentTime) {
        this._updateEndedStauts(player)
        player.pause()
        player.emit('ended')
        if (player.constructor.STATE_CLASS) {
          player.removeClass(player.constructor.STATE_CLASS.ENTER)
          player.removeClass(player.constructor.STATE_CLASS.LOADING)
        }
        player = null
        return
      }
      lastCurrentTime = player.currentTime

      setTimeout(checkEnd, 500)
    }

    checkEnd()
  }

  _updateEndedStauts (player) {
    this._endedStatus.mock = true
    this._endedStatus.currentTime = player.video.currentTime
    const nbRange = player.video.buffered.length
    if (nbRange) {
      try {
        this._endedStatus.bufferEnd = player.video.buffered.end(nbRange - 1)
      } catch (e) {}
    }
  }

  _handleMetadataParsed (type) {
    this.emit(REMUX_EVENTS.REMUX_METADATA, type)
    this._player?.emit(CORE_EVENT.METADATA, type, this.tracks[`${type}Track`].meta)
  }

  _handleSEIParsed (sei) {
    if (this._pluginConfig.seiOnDemand) {
      this._seiOnDemand?.append(sei)
      return
    }
    this._player.emit(CORE_EVENT.SEI, sei)
  }

  _handleDemuxComplete () {
    this.emit(REMUX_EVENTS.REMUX_MEDIA)
  }

  _handleAppendInitSegment () {
    this.state.initSegmentArrived = true
    this.mse.addSourceBuffers()
  }

  _handleMediaSegment () {
    this.mse.addSourceBuffers()
    this.mse.doAppend()
  }

  _handleSourceUpdateEnd () {
    const time = this._player.currentTime
    const video = this._player.video
    const preloadTime = this._player.config.preloadTime || this._pluginConfig.preloadTime // 兼容老版本从config传入preloadTime的写法

    const { length } = video.buffered

    if (length === 0) {
      return
    }

    try {
      const bufferEnd = video.buffered.end(length - 1)

      const lastBufferStart = video.buffered.start(length - 1)
      if (lastBufferStart > this._player.currentTime) {
        this._player.currentTime = lastBufferStart + 0.1
      }

      if (bufferEnd - time > preloadTime * 2 && !this._player.paused) {
        if (bufferEnd - preloadTime > this._player.currentTime) {
          this._player.currentTime = bufferEnd - preloadTime
          this._player.emit(CORE_EVENT.CHASE_FRAME, { bufferEnd, preloadTime })
        }
      }
    } catch (e) {}

    this.mse.doAppend()
    if (this._player.paused || this.urlSwitching) {
      this.urlSwitching = false
      this._handleTimeUpdate()
    }
  }

  _handleTimeUpdate () {
    if (!this._player || !this._player.video) return
    const time = this._player.currentTime

    const video = this._player.video
    const buffered = video.buffered

    if (!buffered || !buffered.length) {
      return
    }

    const range = [0, 0]
    const currentTime = video.currentTime
    if (buffered) {
      for (let i = 0, len = buffered.length; i < len; i++) {
        range[0] = buffered.start(i)
        range[1] = buffered.end(i)
        if (range[0] <= currentTime && currentTime <= range[1]) {
          break
        }
      }
    }

    const bufferStart = range[0]
    const bufferEnd = range[1]

    if (currentTime < bufferStart) {
      // 跳过播放过程中的小洞
      // 兼容Safari bufferStart时间不准确，导致seek失败
      video.currentTime = bufferStart + 0.1
      return
    }

    if (time - bufferStart > 10 || buffered.length > 1) {
      // 在直播时及时清空buffer，降低直播内存占用
      if (this.bufferClearTimer || !this.state.randomAccessPoints.length) {
        return
      }
      let rap = Infinity
      for (let i = 0; i < this.state.randomAccessPoints.length; i++) {
        const temp = Math.ceil(this.state.randomAccessPoints[i] / 1000)
        if (temp > time - 10) {
          break
        } else {
          rap = temp
        }
      }

      // console.log('rap', rap, `time ${time}`, `bufferEnd ${bufferEnd}`,`clean ${Math.min(rap, time - 10, bufferEnd - 10)}`)
      this.mse.remove(Math.max(Math.min(rap, time - 10, bufferEnd - 10), 0.1), 0)

      this.bufferClearTimer = setTimeout(() => {
        this.bufferClearTimer = null
      }, 5000)
    }
  }

  _handleNetworkError (tag, err) {
    this._player.emit('error', {
      code: err.code,
      errorType: 'network',
      type: 'network',
      ex: `[${tag}]: ${err.message}`,
      errd: {}
    })
    this._onError(LOADER_EVENTS.LOADER_ERROR, tag, err, true)
  }

  _handleFetchRetry (tag, info) {
    this._player.emit(CORE_EVENT.LOADRETRY, Object.assign({
      tag
    }, info))
  }

  _handleDemuxError (tag, err, fatal) {
    if (fatal === undefined) {
      fatal = false
    }
    this.pause()
    this._player.emit('error', {
      code: '31',
      errorType: 'parse',
      type: 'parse',
      ex: `[${tag}]: ${err ? err.message : ''}`,
      errd: {}
    })
    this._onError(DEMUX_EVENTS.DEMUX_ERROR, tag, err, fatal)
  }

  _handleRemuxError (tag, err) {
    this.pause()
    this._player.emit('error', {
      code: '31',
      errorType: 'parse',
      type: 'parse',
      ex: `[${tag}]: ${err ? err.message : ''}`,
      errd: {}
    })
    this._onError(REMUX_EVENTS.Remuxer, tag, err)
  }

  _handleMseError (tag, err, fatal) {
    if (fatal === undefined) {
      fatal = false
    }
    this._player.emit('error', {
      code: '31',
      errorType: 'parse',
      type: 'parse',
      ex: `[${tag}]: ${err ? err.message : ''}`,
      errd: {}
    })
    this.pause()
    this._onError(MSE_EVENTS.MSE_ERROR, tag, err, fatal)
  }

  _handleAddRAP (rap) {
    if (this.state.randomAccessPoints) {
      this.state.randomAccessPoints.push(rap)
    }
  }

  _onError (type, mod, err, fatal) {
    const error = {
      code: err.code,
      errorType: type,
      type,
      errorDetails: `[${mod}]: ${err ? err.message : ''}`,
      errorFatal: fatal || false
    }
    this._player.emit(FLV_ERROR, error)
  }

  _endOfStream () {
    this.mse?.endOfStream()
  }

  seek () {
    if (!this.state.initSegmentArrived) {
      this.loadData()
    }
  }

  onceKeyFrame (f) {
    this.once(DEMUX_EVENTS.ISKEYFRAME, f)
  }

  loadData (url = this._player.config.url) {
    const { count: times, delay: delayTime } = this._player.config.retry || {}
    // 兼容player.config上传入retry参数的逻辑
    const retryCount = typeof times === 'undefined' ? this._pluginConfig.retryCount : times
    const retryDelay = typeof delayTime === 'undefined' ? this._pluginConfig.retryDelay : delayTime

    this.emit(LOADER_EVENTS.LADER_START, url, {}, {
      retryCount,
      retryDelay,
      loadTimeout: this._pluginConfig.loadTimeout,
      maxReaderInterval: this._pluginConfig.maxReaderInterval
    })
  }

  pause () {
    const loader = this._context.getInstance('FETCH_LOADER')

    if (loader) {
      loader.cancel()
    }
  }

  destroy () {
    this._player.off('timeupdate', this._handleTimeUpdate)
    this.mse = null
    this._seiOnDemand?.destroy()
    this.state.randomAccessPoints = []
    if (this._timer) clearInterval(this._timer)
  }
}
